// Workload bar — FE-032 / spec §7.0.2.
//
// Per-staff sum of ``complexity`` across open Commitments. Sorted
// descending by workload value; ties keep API order. Each row is a
// click-through to the Commitment Tracker filtered to that staff
// member's open Commitments.

import {
  buildCommitmentFilterUrl,
  type DashboardDateRange,
  type EmployeeAnalyticsResponse,
} from "@/features/dashboard";
import { formatStaffName } from "@/features/dashboard/staffPerformance";

import { DashboardSectionHeader } from "../primitives";

import { HorizontalBar, type HorizontalBarRow } from "./HorizontalBar";

interface WorkloadBarProps {
  rows: EmployeeAnalyticsResponse["workload_bar"];
  dateRange: DashboardDateRange;
}

export function WorkloadBar({ rows, dateRange }: WorkloadBarProps) {
  const sorted = [...rows].sort(
    (a, b) => b.complexity_sum - a.complexity_sum,
  );
  const barRows: HorizontalBarRow[] = sorted.map((row) => {
    const name = formatStaffName(row);
    return {
      id: row.user_id,
      label: name,
      value: row.complexity_sum,
      href: buildCommitmentFilterUrl(
        { assigned_to: row.user_id, has_assignee: true },
        dateRange,
      ),
      ariaLabel: `${name} carries ${row.complexity_sum} open complexity`,
    };
  });
  return (
    <section data-testid="workload-bar" aria-labelledby="workload-bar-heading">
      <DashboardSectionHeader
        title={
          <span id="workload-bar-heading" className="text-sm font-semibold">
            Workload
          </span>
        }
        subtitle="Open Commitment complexity by staff"
      />
      <div className="mt-2">
        <HorizontalBar
          rows={barRows}
          valueSuffix="pts"
          emptyLabel="No open Commitments are assigned to brokerage staff."
        />
      </div>
    </section>
  );
}
